// src/hooks/useAmbassadors.ts - React хук для работы с амбассадорами

import { useState, useEffect } from 'react';
import { message } from 'antd';
import { ambassadorsApi } from '@/services/api/ambassadors';
import { configApi } from '@/services/api/config';  // ✅ ДОБАВЛЕНО
import type {
  AmbassadorProfile,
  AmbassadorDetail,
  AmbassadorSettings,
  CreateAmbassadorRequest,
  UpdateAmbassadorRequest,
  AmbassadorFilters,
  AmbassadorProgramStats,
} from '@/types/models/ambassador';


interface AmbassadorsResponse {
  ambassadors: AmbassadorProfile[];  // ✅
  total_count: number;               // ✅
}
export const useAmbassadors = () => {
  // Состояние данных
  const [ambassadors, setAmbassadors] = useState<AmbassadorProfile[]>([]);
  const [ambassadorsData, setAmbassadorsData] = useState<AmbassadorsResponse | null>(null);
  const [currentAmbassador, setCurrentAmbassador] = useState<AmbassadorDetail | null>(null);
  const [programStats, setProgramStats] = useState<AmbassadorProgramStats | null>(null);
  const [settings, setSettings] = useState<AmbassadorSettings | null>(null);

  // Состояние загрузки
  const [loading, setLoading] = useState(false);
  const [ambassadorsLoading, setAmbassadorsLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);

  // Загрузка списка амбассадоров
  const loadAmbassadors = async (params?: AmbassadorFilters) => {
    setAmbassadorsLoading(true);
    try {
      const response = await ambassadorsApi.getAmbassadors(params);
      setAmbassadorsData(response.data.data);
      setAmbassadors(response.data.data.ambassadors); 
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка загрузки амбассадоров';
      message.error(errorMsg);
      console.error('Error loading ambassadors:', error);
    } finally {
      setAmbassadorsLoading(false);
    }
  };

  // Загрузка деталей амбассадора
  const loadAmbassadorDetail = async (ambassadorId: number) => {
    setDetailLoading(true);
    try {
      const response = await ambassadorsApi.getAmbassador(ambassadorId);
      setCurrentAmbassador(response.data.data);
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка загрузки данных амбассадора';
      message.error(errorMsg);
      console.error('Error loading ambassador detail:', error);
      throw error;
    } finally {
      setDetailLoading(false);
    }
  };

  // Создание нового амбассадора
  const createAmbassador = async (data: CreateAmbassadorRequest) => {
    setLoading(true);
    try {
      const response = await ambassadorsApi.createAmbassador(data);
      message.success('Амбассадор успешно создан');
      
      // Обновляем список
      await refresh();
      
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка создания амбассадора';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Обновление амбассадора
  const updateAmbassador = async (ambassadorId: number, data: UpdateAmbassadorRequest) => {
    setLoading(true);
    try {
      await ambassadorsApi.updateAmbassador(ambassadorId, data);
      message.success('Данные амбассадора обновлены');
      
      // ✅ Перезагружаем список
      await loadAmbassadors();
      
      // Если это текущий амбассадор, обновляем детали
      if (currentAmbassador && currentAmbassador.id === ambassadorId) {
        await loadAmbassadorDetail(ambassadorId);
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка обновления данных';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Удаление амбассадора
  const deleteAmbassador = async (ambassadorId: number) => {
    setLoading(true);
    try {
      await ambassadorsApi.deleteAmbassador(ambassadorId);
      message.success('Амбассадор удален');
      
      // Удаляем из локального состояния
      setAmbassadors(prev => prev.filter(amb => amb.id !== ambassadorId));
      
      return true;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка удаления амбассадора';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Активация амбассадора
  const activateAmbassador = async (ambassadorId: number) => {
    setLoading(true);
    try {
      await ambassadorsApi.activateAmbassador(ambassadorId);
      message.success('Амбассадор активирован');
      
      // ✅ Перезагружаем список
      await loadAmbassadors();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка активации амбассадора';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Деактивация амбассадора
  const deactivateAmbassador = async (ambassadorId: number) => {
    setLoading(true);
    try {
      await ambassadorsApi.deactivateAmbassador(ambassadorId);
      message.success('Амбассадор деактивирован');
      
      // ✅ Перезагружаем список
      await loadAmbassadors();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка деактивации амбассадора';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Корректировка баланса
  const adjustBalance = async (ambassadorId: number, amount: number, description: string) => {
    setLoading(true);
    try {
      const response = await ambassadorsApi.adjustAmbassadorBalance(ambassadorId, {
        amount,
        description,
      });
      
      const actionText = amount > 0 ? 'начислены' : 'списаны';
      message.success(`Бонусы ${actionText}`);
      
      // Обновляем баланс в локальном состоянии
      setAmbassadors(prev =>
        prev.map(amb =>
          amb.id === ambassadorId
            ? { ...amb, ambassador_bonus_balance: response.data.data.new_balance }
            : amb
        )
      );
      
      // Если открыты детали, обновляем их
      if (currentAmbassador && currentAmbassador.id === ambassadorId) {
        await loadAmbassadorDetail(ambassadorId);
      }
      
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка корректировки баланса';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Загрузка статистики программы
  const loadProgramStats = async (params?: {
    date_from?: string;
    date_to?: string;
  }) => {
    setStatsLoading(true);
    try {
      const response = await ambassadorsApi.getProgramStats(params);
      setProgramStats(response.data.data);
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка загрузки статистики';
      message.error(errorMsg);
      console.error('Error loading program stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  // Загрузка настроек программы
  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await ambassadorsApi.getSettings();
      const settingsData = response.data.data;
      
      // ✅ Если есть welcome_gift_item, загружаем детали блюда
      if (settingsData.welcome_gift_item) {
        try {
          const itemResponse = await configApi.getMenuItem(settingsData.welcome_gift_item);
          const itemData = itemResponse.data.data.item;
          
          // Добавляем детали блюда в настройки
          settingsData.welcome_gift_item_details = {
            id: itemData.id,
            name: itemData.name,
            price: itemData.price,
            stock_available: itemData.stock_available,
            image_url: itemData.image_url,
          };
        } catch (error) {
          console.error('Error loading gift item details:', error);
          // Не блокируем загрузку настроек если не удалось загрузить блюдо
        }
      }
      
      setSettings(settingsData);
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка загрузки настроек';
      message.error(errorMsg);
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Обновление настроек программы
  const updateSettings = async (data: Partial<AmbassadorSettings>) => {
    setLoading(true);
    try {
      await ambassadorsApi.updateSettings(data);
      message.success('Настройки обновлены');
      
      // ✅ Перезагружаем настройки чтобы получить детали нового блюда
      await loadSettings();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка обновления настроек';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Массовое обновление статуса
  const bulkUpdateStatus = async (ambassadorIds: number[], isActive: boolean) => {
    setLoading(true);
    try {
      const response = await ambassadorsApi.bulkUpdateStatus({
        ambassador_ids: ambassadorIds,
        is_active: isActive,
      });
      
      const actionText = isActive ? 'активированы' : 'деактивированы';
      message.success(`${response.data.data.updated_count} амбассадоров ${actionText}`);
      
      // Обновляем в локальном состоянии
      setAmbassadors(prev =>
        prev.map(amb =>
          ambassadorIds.includes(amb.id) ? { ...amb, is_active: isActive } : amb
        )
      );
      
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка массового обновления';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Валидация промокода
  const validatePromoCode = async (promoCode: string) => {
    try {
      const response = await ambassadorsApi.validatePromoCode(promoCode);
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка проверки промокода';
      message.error(errorMsg);
      throw error;
    }
  };

  // Экспорт данных
  const exportData = async (params?: AmbassadorFilters) => {
    setLoading(true);
    try {
      const response = await ambassadorsApi.exportAmbassadors(params);
      
      // Создаем ссылку для скачивания
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ambassadors_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      message.success('Данные экспортированы');
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка экспорта данных';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Очистка деталей амбассадора
  const clearAmbassadorDetail = () => {
    setCurrentAmbassador(null);
  };

  // Перезагрузка текущих данных
  const refresh = async () => {
    await loadAmbassadors();  // ✅ Просто перезагружаем без параметров
  };

  // Загрузка базовых данных при монтировании
  useEffect(() => {
    loadAmbassadors();
  }, []);

  return {
    // Данные
    ambassadors,
    ambassadorsData,
    currentAmbassador,
    programStats,
    settings,

    // Состояние загрузки
    loading,
    ambassadorsLoading,
    detailLoading,
    statsLoading,

    // Методы CRUD
    loadAmbassadors,
    loadAmbassadorDetail,
    createAmbassador,
    updateAmbassador,
    deleteAmbassador,

    // Методы управления статусом
    activateAmbassador,
    deactivateAmbassador,
    bulkUpdateStatus,

    // Методы работы с балансом
    adjustBalance,

    // Методы работы со статистикой и настройками
    loadProgramStats,
    loadSettings,
    updateSettings,

    // Дополнительные методы
    validatePromoCode,
    exportData,
    clearAmbassadorDetail,
    refresh,
  };
};